import React, { useEffect, useState } from "react";
import { Activity, RefreshCw, Rocket, SignalHigh, Target, Workflow } from "lucide-react";

import { MainLayout } from "../components/layout/MainLayout";
import EventSignalPanel from "../components/ops/EventSignalPanel";
import JobHealthPanel from "../components/ops/JobHealthPanel";
import NormalizationPanel from "../components/ops/NormalizationPanel";
import SourceCoveragePanel from "../components/ops/SourceCoveragePanel";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { formatRelativeTime } from "../lib/utils";
import { opsApi } from "../lib/opsApi";

const emptySnapshot = {
  loadedAt: null,
  targetStartups: 15000,
  summary: {
    totalStartups: 0,
    autoSourced: 0,
    userAdded: 0,
    remainingStartups: 15000,
    progressPercent: 0,
    sampledCount: 0,
    sampleIsPartial: false,
    recentSignalCount: 0,
    availableProviderCount: 0,
    totalProviderCount: 0,
    lastBackfillAt: null,
  },
  sourceCoverage: {
    sampledCount: 0,
    totalStartups: 0,
    isSampled: false,
    families: [],
  },
  normalization: {
    sampledCount: 0,
    totalStartups: 0,
    isSampled: false,
    overallCoveragePercent: 0,
    fields: [],
  },
  jobHealth: {
    database: {
      status: "unknown",
      label: "unknown",
      checkedAt: null,
    },
    providers: [],
    availableProviderCount: 0,
    totalProviderCount: 0,
    backfill: {
      status: "empty",
      scrapedCount: 0,
      newCount: 0,
      updatedCount: 0,
      errorCount: 0,
      startedAt: null,
      completedAt: null,
    },
    queueMetricsAvailable: false,
  },
  signalMetrics: {
    sampledStartupCount: 0,
    startupsWithSignals: 0,
    startupSignalCoveragePercent: 0,
    recentSignalCount: 0,
    latestSignalAt: null,
    byType: [],
    freshness: [],
  },
  meta: {
    hasAnyData: false,
    warnings: [],
  },
};

const SummaryTile = ({ label, value, detail, icon: Icon }) => (
  <div className="rounded-2xl border border-white/5 bg-zinc-950/70 p-4">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <Icon className="h-4 w-4 text-zinc-400" />
    </div>
    <p className="mt-4 font-mono text-2xl text-zinc-50">{value}</p>
    <p className="mt-1 text-sm font-['Manrope'] text-zinc-500">{detail}</p>
  </div>
);

const HeroSkeleton = () => (
  <div className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-6 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
    <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
      <div className="space-y-3">
        <Skeleton className="h-6 w-36 bg-white/10" />
        <Skeleton className="h-10 w-72 bg-white/10" />
        <Skeleton className="h-4 w-[28rem] max-w-full bg-white/10" />
      </div>
      <div className="grid w-full gap-3 sm:grid-cols-3 xl:max-w-xl">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-white/5 bg-zinc-950/70 p-4">
            <Skeleton className="h-3 w-20 bg-white/10" />
            <Skeleton className="mt-4 h-6 w-16 bg-white/10" />
            <Skeleton className="mt-2 h-3 w-24 bg-white/10" />
          </div>
        ))}
      </div>
    </div>
    <div className="mt-6 rounded-2xl border border-white/5 bg-zinc-950/70 p-5">
      <div className="flex items-end justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-28 bg-white/10" />
          <Skeleton className="h-10 w-40 bg-white/10" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-8 w-20 bg-white/10" />
          <Skeleton className="h-3 w-24 bg-white/10" />
        </div>
      </div>
      <Skeleton className="mt-4 h-3 w-full rounded-full bg-white/10" />
    </div>
  </div>
);

const OpsPage = () => {
  const [snapshot, setSnapshot] = useState(emptySnapshot);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [runningAction, setRunningAction] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  const loadSnapshot = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const nextSnapshot = await opsApi.getWeek2Snapshot();
      setSnapshot(nextSnapshot);
      setError(null);
    } catch (err) {
      console.error("Failed to load Week 2 ops snapshot:", err);
      setError("Failed to load Week 2 ops snapshot");
      setSnapshot((current) => (current.meta.hasAnyData ? current : emptySnapshot));
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, []);

  const runAction = async ({ key, runner, successMessage }) => {
    setRunningAction(key);
    try {
      const result = await runner();
      setActionResult({
        tone: "success",
        title: successMessage,
        detail: result?.run_id || result?.status || result?.message || "Background work queued.",
      });
      await loadSnapshot({ silent: true });
    } catch (err) {
      console.error(`Failed to run ops action ${key}:`, err);
      setActionResult({
        tone: "error",
        title: "Action failed",
        detail: err?.response?.data?.detail || err?.message || "Unknown error",
      });
    } finally {
      setRunningAction(null);
    }
  };

  const summary = snapshot.summary || emptySnapshot.summary;
  const warningText = snapshot.meta?.warnings?.join(" ");
  const progressWidth = summary.progressPercent > 0 ? `${Math.max(summary.progressPercent, 1)}%` : "0%";

  return (
    <MainLayout>
      <div className="mx-auto max-w-7xl space-y-8 p-6" data-testid="ops-page">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-300">
              Week 2 Internal Ops
            </div>
            <h1 className="mt-4 text-3xl font-light font-['Outfit'] text-zinc-50">Sourcing operator console</h1>
            <p className="mt-2 font-['Manrope'] text-zinc-400">
              Coverage, normalization, crawl health, and signal freshness for the Week 2 sourcing pipeline.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="rounded-full border border-zinc-800 bg-zinc-950/70 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-400">
              {snapshot.loadedAt ? `Updated ${formatRelativeTime(snapshot.loadedAt)}` : "Waiting for first snapshot"}
            </div>
            <Button
              variant="outline"
              onClick={() => loadSnapshot({ silent: true })}
              data-testid="ops-refresh-btn"
              className="border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-50"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading || refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-400/20 bg-rose-400/5 px-4 py-3 text-sm font-['Manrope'] text-rose-200">
            {error}
          </div>
        )}

        {!loading && warningText && (
          <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-sm font-['Manrope'] text-amber-100">
            {warningText}
          </div>
        )}

        {loading ? (
          <HeroSkeleton />
        ) : (
          <>
            <section className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-6 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
              <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                <div className="max-w-3xl">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Progress Toward 15K</p>
                  <h2 className="mt-2 font-['Outfit'] text-2xl text-zinc-50">
                    {summary.totalStartups.toLocaleString()} startups indexed
                  </h2>
                  <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">
                    Remaining to target: {summary.remainingStartups.toLocaleString()}
                    {summary.sampleIsPartial && ` · coverage panels sampled from the latest ${summary.sampledCount.toLocaleString()} records`}
                  </p>
                </div>

                <div className="grid w-full gap-3 sm:grid-cols-3 xl:max-w-xl">
                  <SummaryTile
                    label="Auto sourced"
                    value={summary.autoSourced.toLocaleString()}
                    detail="Ingested by automated sourcing"
                    icon={Workflow}
                  />
                  <SummaryTile
                    label="Providers up"
                    value={`${summary.availableProviderCount}/${summary.totalProviderCount}`}
                    detail="Available provider integrations"
                    icon={Activity}
                  />
                  <SummaryTile
                    label="Recent signals"
                    value={summary.recentSignalCount.toLocaleString()}
                    detail="Current signal stream window"
                    icon={SignalHigh}
                  />
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-white/5 bg-zinc-950/70 p-5" data-testid="ops-startup-progress">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Startup coverage</p>
                    <div className="mt-2 flex items-end gap-3">
                      <p className="font-mono text-4xl text-zinc-50">{summary.totalStartups.toLocaleString()}</p>
                      <p className="pb-1 text-sm font-mono text-zinc-500">/ {snapshot.targetStartups.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-mono text-3xl text-zinc-50">{summary.progressPercent}%</p>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">
                      {summary.lastBackfillAt ? `Last backfill ${formatRelativeTime(summary.lastBackfillAt)}` : "No backfill recorded"}
                    </p>
                  </div>
                </div>

                <div className="mt-4 h-3 rounded-full bg-zinc-800">
                  <div className="h-3 rounded-full bg-white transition-all duration-300" style={{ width: progressWidth }} />
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <SummaryTile
                    label="User added"
                    value={summary.userAdded.toLocaleString()}
                    detail="Records created manually"
                    icon={Target}
                  />
                  <SummaryTile
                    label="Sample size"
                    value={summary.sampledCount.toLocaleString()}
                    detail={summary.sampleIsPartial ? "Latest records sampled" : "Full startup universe in scope"}
                    icon={Activity}
                  />
                  <SummaryTile
                    label="Remaining"
                    value={summary.remainingStartups.toLocaleString()}
                    detail="Still needed to hit 15k"
                    icon={Target}
                  />
                  <SummaryTile
                    label="Snapshot age"
                    value={snapshot.loadedAt ? formatRelativeTime(snapshot.loadedAt) : "Unknown"}
                    detail="Operator console refresh age"
                    icon={RefreshCw}
                  />
                </div>
              </div>
            </section>

            <section className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-6 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-3xl">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Operator Actions</p>
                  <h2 className="mt-2 font-['Outfit'] text-2xl text-zinc-50">Autonomous sourcing controls</h2>
                  <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">
                    Queue a wider VC discovery sweep or a queue-only enrichment pass directly from the ops surface.
                  </p>
                </div>

                {actionResult && (
                  <div
                    className={`rounded-2xl border px-4 py-3 text-sm font-['Manrope'] ${
                      actionResult.tone === "error"
                        ? "border-rose-400/20 bg-rose-400/5 text-rose-200"
                        : "border-emerald-400/20 bg-emerald-400/5 text-emerald-100"
                    }`}
                  >
                    <p className="font-medium">{actionResult.title}</p>
                    <p className="mt-1 text-xs opacity-90">{actionResult.detail}</p>
                  </div>
                )}
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-white/5 bg-zinc-950/70 p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cyan-400/10 text-cyan-300">
                      <Rocket className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium font-['Outfit'] text-zinc-50">Run VC fund discovery sweep</p>
                      <p className="text-xs font-['Manrope'] text-zinc-500">Background staged discovery across more fund portfolio pages.</p>
                    </div>
                  </div>
                  <Button
                    className="mt-4 w-full bg-white text-zinc-950 hover:bg-zinc-200"
                    disabled={runningAction !== null}
                    onClick={() =>
                      runAction({
                        key: "discovery",
                        runner: () => opsApi.startAutonomousDiscovery(),
                        successMessage: "VC fund discovery queued",
                      })
                    }
                  >
                    <Rocket className={`mr-2 h-4 w-4 ${runningAction === "discovery" ? "animate-pulse" : ""}`} />
                    {runningAction === "discovery" ? "Queuing discovery..." : "Queue discovery"}
                  </Button>
                </div>

                <div className="rounded-2xl border border-white/5 bg-zinc-950/70 p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-400/10 text-emerald-300">
                      <Target className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium font-['Outfit'] text-zinc-50">Queue enrichment batch</p>
                      <p className="text-xs font-['Manrope'] text-zinc-500">Create crawl jobs for the next startup batch without forcing live execution.</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="mt-4 w-full border-zinc-800 text-zinc-200 hover:bg-zinc-900"
                    disabled={runningAction !== null}
                    onClick={() =>
                      runAction({
                        key: "enrichment",
                        runner: () => opsApi.queueAutonomousEnrichment(),
                        successMessage: "Enrichment batch queued",
                      })
                    }
                  >
                    <Target className={`mr-2 h-4 w-4 ${runningAction === "enrichment" ? "animate-pulse" : ""}`} />
                    {runningAction === "enrichment" ? "Queuing enrichment..." : "Queue enrichment"}
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}

        <div className="grid gap-6 xl:grid-cols-2">
          <SourceCoveragePanel coverage={snapshot.sourceCoverage} loading={loading} />
          <NormalizationPanel normalization={snapshot.normalization} loading={loading} />
          <JobHealthPanel jobHealth={snapshot.jobHealth} loading={loading} />
          <EventSignalPanel signalMetrics={snapshot.signalMetrics} loading={loading} />
        </div>
      </div>
    </MainLayout>
  );
};

export default OpsPage;
